"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224157" className="hidden md:flex gap-6">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex z-50 overflow-visible">
      <NavigationMenuList className="gap-6 overflow-visible">
        {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        return <NavigationMenuItem key={item.id} data-index={i} className="relative group">
              {hasChildren ? <>
                  <Button variant="ghost" className="flex items-center gap-1 text-foreground font-default font-medium text-base hover:text-ring transition-colors cursor-pointer py-2 h-auto px-0 hover:bg-transparent" style={{
              fontFamily: 'inherit',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              letterSpacing: 'inherit',
              color: 'inherit'
            }}>
                    {item.label}
                    <ChevronDown className="size-4 transition-transform duration-200 group-hover:rotate-180" />
                  </Button>
                  <div className="absolute top-full left-0 min-w-[200px] pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50">
                    <div className="bg-popover border border-border rounded-md shadow-lg p-2 flex flex-col gap-1">
                      {item.children?.map(child => <NavigationMenuLink key={child.id} asChild>
                          <Link to={child.href || "#"} className="block px-4 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>)}
                    </div>
                  </div>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="text-foreground font-default font-medium text-base hover:text-ring transition-colors py-2 block">
                    {item.label}
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuContent({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col gap-4 py-4">
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-4 py-4 max-h-[80vh] overflow-y-auto">
      <nav className="flex flex-col gap-2">
        {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItems[item.id];
        return <div key={item.id} data-index={i} className="flex flex-col border-b border-border/50">
              <div className="flex items-center justify-between">
                <Link to={item.href || "#"} className="text-foreground font-default font-semibold text-lg py-3 pl-4 flex-1" onClick={onNavigate}>
                  {item.label}
                </Link>
                {hasChildren && <Button variant="ghost" size="icon" onClick={() => toggleExpand(item.id)} className="text-muted-foreground hover:text-foreground transition-colors">
                    <ChevronDown className={cn("size-5 transition-transform duration-200", isExpanded && "rotate-180")} />
                  </Button>}
              </div>
              {hasChildren && <div className={cn("overflow-hidden transition-all duration-200 ease-in-out flex flex-col bg-accent/5", isExpanded ? "max-h-[500px] pb-2" : "max-h-0")}>
                  {item.children?.map(child => <Link key={child.id} to={child.href || "#"} className="text-foreground/80 font-default font-medium text-base py-2 pl-8 hover:text-ring transition-colors" onClick={onNavigate}>
                      {child.label}
                    </Link>)}
                </div>}
            </div>;
      })}
      </nav>
      <div className="pt-4 flex justify-center">
        <Button variant="default" size="lg" className="w-[calc(100%-30px)] font-default font-semibold hover:scale-[1.02] transition-transform" asChild onClick={onNavigate}>
          <Link to="https://legal-spider.websiteshop.au/contact/">Book Consultation</Link>
        </Button>
      </div>
    </div>;
}
export default function GatewayNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <MenuProvider menu_id="2" className="container mx-auto px-4 md:px-6 lg:px-8 h-20 flex items-center justify-between overflow-visible">
        {}
        <Link to="/" className="flex-shrink-0 z-50 flex items-center">
          <img src="https://legal-spider.websiteshop.au/wp-content/uploads/2026/04/SLA-Logo.avif" alt="SLA Finance Group" className="h-8 w-auto" />
        </Link>

        {}
        <DesktopMenu />

        {}
        <div className="hidden md:flex flex-shrink-0">
          <Button variant="default" className="font-default font-semibold hover:scale-[1.02] transition-transform" asChild>
            <Link to="https://legal-spider.websiteshop.au/contact/">Book Consultation</Link>
          </Button>
        </div>

        {}
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" className="text-foreground">
              <Menu className="size-6" />
              <span className="sr-only">Toggle navigation menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="top" className="w-full border-b border-border bg-background p-0">
            <MobileMenuContent onNavigate={() => setIsMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
      </MenuProvider>
    </section>;
}